/**
 * @module memoizer
 * @private
 * @since 2.0.0
 */

/**
 * @typedef ObjectProperty
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty
 * @type     {Object}
 * @property {boolean}  [configurable=true]
 * @property {boolean}  [enumerable=true]
 * @property {boolean}  [writable=true]
 * @property {any}      [value=undefined]
 * @property {function} [get=undefined]
 * @property {function} [set=undefined]
 */

/**
 * OBJECT_PROPERTY_ATTRIBUTES attributes of an ObjectProperty
 * @type {Array}
 */
const OBJECT_PROPERTY_ATTRIBUTES = [
    'get',
    'value',
    'set',
    'configurable',
    'enumerable',
    'writable'
];

/**
 * Attributes to convert to memoised attributes
 * @type {Array}
 */
const SHOULD_MEMOISE = [
    'get',
    'value'
];

/**
 * Memoize return values of function properties and getters for a given object.
 * @param  {Object}   instance The object to memoize.
 * @param  {String[]} props    Properties to wrap in memoizers
 *
 * @example
 * class Klass {
 *   constructor(initial_value = 'nothing') {
 *      this.variable = initial_value;
 *      memoise(this, 'a_getter', 'a_function');
 *   }
 *
 *   // I will be memoised
 *   get a_getter() {
 *     return this.variable;
 *   }
 *
 *   // I will not be memoised
 *   get a_dynamic_getter() {
 *     return this.variable;
 *   }
 *
 *   // I will be memoised
 *   a_function() {
 *     return `The value is ${this.variable}`;
 *   }
 * }
 */
export function memoizer(instance, ...props) {

    /**
     * Private memory store
     * @type {Object}
     */
    const memory = {};

    /**
     * memoize values of operations by key
     * @private
     * @param  {String}   item     Key of item to memoize
     * @param  {Function} retreive Operation to calculate the value
     * @return {Any}
     */
    function memoize(item, retreive) {
        return memory[item] = item in memory ? memory[item] : retreive();
    }

    props.forEach(
        (property) => assignDescriptorProperties.call(instance, property, memoize)
    );

}

/**
 * Assigns new descriptor properties. Getter and Values will be wrapped in a memoize method
 * @param  {String}   property Property name
 * @param  {Function} memoize  How to memoize the result
 * no return value
 */
function assignDescriptorProperties(property, memoize) {
    const descriptor = Object.getOwnPropertyDescriptor(this.constructor.prototype, property);

    const attributes = OBJECT_PROPERTY_ATTRIBUTES.reduce(
        (attributes, attribute) => {
            switch (typeof descriptor[attribute]) {
                case 'undefined':

                    // do nothing
                    break;
                case 'function':
                    if (SHOULD_MEMOISE.includes(attribute)) {
                        attributes[attribute] = () => memoize(
                            `${property}$${attribute}`,
                            descriptor[attribute].bind(this)
                        );
                    } else {
                        attributes[attribute] = descriptor[attribute];
                    }
                    break;
                default:
                    attributes[attribute] = descriptor[attribute];
                    break;
            }

            return attributes;
        },
        {}
    );

    Object.defineProperty(
        this,
        property,
        attributes
    );
}
